import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { longTermApi, customerApi, vehicleApi, formatApiError } from '../services/api';
import { Timer, Plus, FileText, Buildings, CurrencyEur, ClockCounterClockwise, Car } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const STATUS_LABEL = {
  draft: { label: 'Entwurf', color: 'bg-[#F5F7FA] text-[#525866]' },
  active: { label: 'Aktiv', color: 'bg-emerald-100 text-emerald-700' },
  terminated: { label: 'Gekündigt', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', color: 'bg-amber-100 text-amber-700' },
};

const fmtEUR = (v) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const initialForm = {
  customer_id: '',
  vehicle_id: '',
  start_date: '',
  end_date: '',
  billing_mode: 'monthly',
  price_model: 'monthly_flat_km',
  monthly_rate: '',
  included_km_per_month: 1500,
  extra_km_price: 0.15,
  daily_rate: '',
  deposit_amount: '',
  pickup_mileage: 0,
  notice_period_months: 3,
  auto_renewal: false,
  payment_method: 'ueberweisung',
  services: {
    insurance: true,
    maintenance: true,
    tires: true,
    fuel_card: false,
    replacement_car: false,
    roadside_assistance: true,
  },
  notes: '',
};

const SERVICE_OPTIONS = [
  { key: 'insurance', label: 'Kaskoversicherung' },
  { key: 'maintenance', label: 'Wartung & Inspektion' },
  { key: 'tires', label: 'Reifen (Sommer/Winter)' },
  { key: 'roadside_assistance', label: 'Pannenhilfe 24/7' },
  { key: 'fuel_card', label: 'Tankkarte' },
  { key: 'replacement_car', label: 'Ersatzfahrzeug' },
];

export default function LongTermContracts() {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, sRes, custRes, vehRes] = await Promise.all([
        longTermApi.list(),
        longTermApi.stats(),
        customerApi.getAll(),
        vehicleApi.getAll(),
      ]);
      setContracts(cRes.data || []);
      setStats(sRes.data);
      setCustomers(custRes.data || []);
      setVehicles(vehRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        monthly_rate: form.price_model === 'monthly_flat_km' ? Number(form.monthly_rate) : null,
        daily_rate: form.price_model === 'daily_rate' ? Number(form.daily_rate) : null,
        included_km_per_month: Number(form.included_km_per_month || 0),
        extra_km_price: Number(form.extra_km_price || 0),
        deposit_amount: Number(form.deposit_amount || 0),
        pickup_mileage: Number(form.pickup_mileage || 0),
        notice_period_months: Number(form.notice_period_months || 3),
        end_date: form.end_date || null,
      };
      await longTermApi.create(payload);
      toast.success('Langzeitmietvertrag angelegt');
      setShowModal(false);
      setForm(initialForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = statusFilter === 'all' ? contracts : contracts.filter((c) => c.status === statusFilter);

  return (
    <div className="p-6 space-y-6" data-testid="long-term-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Langzeitmiete</h1>
          <p className="text-[#525866] mt-1">{contracts.length} Langzeitmietverträge insgesamt</p>
        </div>
        <Button
          className="btn-primary"
          onClick={() => { setForm(initialForm); setShowModal(true); }}
          data-testid="add-long-term-btn"
        >
          <Plus weight="bold" className="mr-2" size={18} />
          Neuer Vertrag
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card-swiss p-5" data-testid="stat-active-contracts">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Timer weight="bold" size={18} />
              </div>
              <span className="overline">Aktive Verträge</span>
            </div>
            <p className="text-3xl font-bold text-[#111418]">{stats.active_contracts}</p>
          </div>
          <div className="card-swiss p-5" data-testid="stat-mrr">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <CurrencyEur weight="bold" size={18} />
              </div>
              <span className="overline">Monatlicher Umsatz (MRR)</span>
            </div>
            <p className="text-3xl font-bold text-[#111418]">{fmtEUR(stats.monthly_recurring_revenue)}</p>
          </div>
          <div className="card-swiss p-5" data-testid="stat-upcoming-billing">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded bg-amber-50 text-amber-600 flex items-center justify-center">
                <ClockCounterClockwise weight="bold" size={18} />
              </div>
              <span className="overline">Anstehende Rechnungen (30 Tage)</span>
            </div>
            <p className="text-3xl font-bold text-[#111418]">{stats.upcoming_invoices?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap" data-testid="status-filter-bar">
        {[
          { k: 'all', l: 'Alle' },
          { k: 'draft', l: 'Entwurf' },
          { k: 'active', l: 'Aktiv' },
          { k: 'terminated', l: 'Gekündigt' },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setStatusFilter(s.k)}
            data-testid={`filter-${s.k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
              statusFilter === s.k ? 'bg-[#111418] text-white border-[#111418]' : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {Array(4).fill().map((_, i) => <div key={i} className="card-swiss h-20 skeleton" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Timer weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Noch keine Langzeitmietverträge angelegt.</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Vertrag</th>
                <th>Kunde</th>
                <th>Fahrzeug</th>
                <th>Laufzeit</th>
                <th>Konditionen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const s = STATUS_LABEL[c.status] || STATUS_LABEL.draft;
                const isBiz = c.customer?.customer_type === 'business' || !!c.customer?.company_name;
                const custDisplay = isBiz ? c.customer?.company_name : `${c.customer?.first_name || ''} ${c.customer?.last_name || ''}`.trim();
                return (
                  <tr key={c.id} data-testid={`long-term-row-${c.id}`}>
                    <td>
                      <Link to={`/long-term/${c.id}`} className="hover:underline font-semibold text-[#111418]" data-testid={`long-term-link-${c.id}`}>
                        {c.contract_number}
                      </Link>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isBiz && <Buildings weight="bold" size={14} className="text-indigo-500" />}
                        <span className="text-sm text-[#111418]">{custDisplay || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-sm text-[#525866]">
                        <Car weight="bold" size={14} />
                        {c.vehicle?.brand} {c.vehicle?.model} · {c.vehicle?.license_plate}
                      </div>
                    </td>
                    <td className="text-sm text-[#525866]">
                      {c.start_date} → {c.end_date || 'unbefristet'}
                      <div className="text-xs text-[#8A94A6]">{c.duration_days} Tage</div>
                    </td>
                    <td className="text-sm">
                      {c.price_model === 'monthly_flat_km' ? (
                        <>
                          <div className="font-semibold text-[#111418]">{fmtEUR(c.monthly_rate)}/Monat</div>
                          <div className="text-xs text-[#8A94A6]">{c.included_km_per_month?.toLocaleString('de-DE')} km inkl.</div>
                        </>
                      ) : (
                        <div className="font-semibold text-[#111418]">{fmtEUR(c.daily_rate)}/Tag</div>
                      )}
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${s.color}`} data-testid={`status-badge-${c.id}`}>
                        {s.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Neuer Langzeitmietvertrag</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Kunde + Fahrzeug */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kunde *</Label>
                <select
                  className="input-swiss w-full"
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  required
                  data-testid="lt-customer-select"
                >
                  <option value="">Kunde wählen…</option>
                  {customers.map((c) => {
                    const bizName = c.company_name;
                    return (
                      <option key={c.id} value={c.id}>
                        {bizName ? `${bizName} (${c.first_name} ${c.last_name})` : `${c.first_name} ${c.last_name}`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <Label className="overline">Fahrzeug *</Label>
                <select
                  className="input-swiss w-full"
                  value={form.vehicle_id}
                  onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                  required
                  data-testid="lt-vehicle-select"
                >
                  <option value="">Fahrzeug wählen…</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.license_plate}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Laufzeit */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Vertragsbeginn *</Label>
                <Input type="date" value={form.start_date} required onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-swiss" data-testid="lt-start-date" />
              </div>
              <div>
                <Label className="overline">Vertragsende (leer = unbefristet)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-swiss" data-testid="lt-end-date" />
              </div>
            </div>

            {/* Abrechnung + Preismodell */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Abrechnung</Label>
                <select
                  className="input-swiss w-full"
                  value={form.billing_mode}
                  onChange={(e) => setForm({ ...form, billing_mode: e.target.value })}
                  data-testid="lt-billing-mode"
                >
                  <option value="monthly">Monatlich (wiederkehrend)</option>
                  <option value="upfront">Einmalig im Voraus</option>
                </select>
              </div>
              <div>
                <Label className="overline">Preismodell</Label>
                <select
                  className="input-swiss w-full"
                  value={form.price_model}
                  onChange={(e) => setForm({ ...form, price_model: e.target.value })}
                  data-testid="lt-price-model"
                >
                  <option value="monthly_flat_km">Monatspauschale + Kilometerpaket</option>
                  <option value="daily_rate">Tagessatz × Tage</option>
                </select>
              </div>
            </div>

            {/* Preise */}
            {form.price_model === 'monthly_flat_km' ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="overline">Monatsrate (Brutto) *</Label>
                  <Input type="number" step="0.01" min="0" value={form.monthly_rate} required onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })} className="input-swiss" data-testid="lt-monthly-rate" placeholder="799.00" />
                </div>
                <div>
                  <Label className="overline">Inkl. km/Monat</Label>
                  <Input type="number" min="0" value={form.included_km_per_month} onChange={(e) => setForm({ ...form, included_km_per_month: e.target.value })} className="input-swiss" data-testid="lt-included-km" />
                </div>
                <div>
                  <Label className="overline">Mehrkm-Preis (€/km)</Label>
                  <Input type="number" step="0.01" min="0" value={form.extra_km_price} onChange={(e) => setForm({ ...form, extra_km_price: e.target.value })} className="input-swiss" data-testid="lt-extra-km-price" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="overline">Tagessatz (Brutto) *</Label>
                <Input type="number" step="0.01" min="0" value={form.daily_rate} required onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} className="input-swiss" data-testid="lt-daily-rate" />
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">Kaution (€)</Label>
                <Input type="number" step="0.01" min="0" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} className="input-swiss" data-testid="lt-deposit" />
              </div>
              <div>
                <Label className="overline">Km-Stand bei Übergabe</Label>
                <Input type="number" min="0" value={form.pickup_mileage} onChange={(e) => setForm({ ...form, pickup_mileage: e.target.value })} className="input-swiss" data-testid="lt-pickup-mileage" />
              </div>
              <div>
                <Label className="overline">Kündigungsfrist (Monate)</Label>
                <Input type="number" min="0" max="24" value={form.notice_period_months} onChange={(e) => setForm({ ...form, notice_period_months: e.target.value })} className="input-swiss" data-testid="lt-notice-months" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Zahlungsart</Label>
                <select
                  className="input-swiss w-full"
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  data-testid="lt-payment-method"
                >
                  <option value="ueberweisung">Überweisung</option>
                  <option value="sepa">SEPA-Lastschrift</option>
                  <option value="bar">Bar</option>
                  <option value="ec">EC-Karte</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="lt-auto-renewal">
                  <input
                    type="checkbox"
                    checked={form.auto_renewal}
                    onChange={(e) => setForm({ ...form, auto_renewal: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-[#525866]">Automatische Verlängerung</span>
                </label>
              </div>
            </div>

            {/* Enthaltene Leistungen */}
            <div>
              <Label className="overline">Enthaltene Leistungen</Label>
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="lt-services-grid">
                {SERVICE_OPTIONS.map((s) => (
                  <label key={s.key} className="inline-flex items-center gap-2 p-2 bg-[#F8F9FB] rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.services[s.key]}
                      onChange={(e) => setForm({ ...form, services: { ...form.services, [s.key]: e.target.checked } })}
                      className="w-4 h-4"
                      data-testid={`lt-service-${s.key}`}
                    />
                    <span className="text-sm text-[#111418]">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="overline">Notizen / Zusatzvereinbarungen</Label>
              <textarea
                className="input-swiss w-full px-3 py-2 min-h-[70px]"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="lt-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="lt-save-btn">
                {submitting ? 'Speichern…' : 'Vertrag anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
